import React, { useEffect, useState } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Chip,
  SelectChangeEvent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
} from '@mui/material';
import {
  LocationOn as LocationOnIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/api';

interface ShippingAddress {
  fullName?: string;
  city: string;
  street: string;
  streetNumber: string;
  flat?: string;
  notes?: string;
  phone: string;
}

interface Order {
  _id: string;
  customerId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  productType: string;
  item: string;
  amount: number;
  status: 'preparing' | 'completed' | 'refunded' | 'cancelled';
  time: string;
  paymentId?: string;
  shippingAddress?: ShippingAddress;
  productDetails?: {
    title: string;
    description: string;
    image: string;
    downloadPath?: string;
  };
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface ApiResponse {
  success: boolean;
  data: Order[];
  pagination: Pagination;
}

export const OrdersManagement: React.FC = () => {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const isRTL = currentLang === 'he';
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 0,
    limit: 10,
    pages: 0,
  });
  const [sortBy, setSortBy] = useState('time');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [addressOrder, setAddressOrder] = useState<Order | null>(null);
  const [updatingOrderId, setUpdatingOrderId] = useState<string | null>(null);

  const fetchOrders = async (page: number, limit: number, sortBy: string, sortOrder: string) => {
    try {
      setLoading(true);
      const response = await api.get<ApiResponse>(
        `/orders?page=${page + 1}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`
      );
      setOrders(response.data.data);
      setPagination(response.data.pagination);
      setError(null);
    } catch (err) {
      setError(t('admin.orders.error'));
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(0, 10, sortBy, sortOrder);
  }, [sortBy, sortOrder]);

  const handleChangePage = (_: unknown, newPage: number) => {
    fetchOrders(newPage, pagination.limit, sortBy, sortOrder);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    fetchOrders(0, newLimit, sortBy, sortOrder);
  };

  const handleSortByChange = (event: SelectChangeEvent) => {
    setSortBy(event.target.value);
  };

  const handleSortOrderChange = (event: SelectChangeEvent) => {
    setSortOrder(event.target.value as 'asc' | 'desc');
  };

  const handleMarkCompleted = async (orderId: string) => {
    try {
      setUpdatingOrderId(orderId);
      await api.patch(`/orders/${orderId}/status`, { status: 'completed' });
      setOrders((prev) =>
        prev.map((order) =>
          order._id === orderId ? { ...order, status: 'completed' } : order
        )
      );
    } catch (err) {
      console.error('Error updating order status:', err);
      setError(t('admin.orders.markCompletedError'));
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'preparing':
        return 'warning';
      case 'refunded':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getProductTypeLabel = (type: string) => {
    switch (type) {
      case 'Tutorial':
        return t('checkout.productType.tutorial');
      case 'Workshop':
        return t('checkout.productType.workshop');
      case 'Item':
        return t('checkout.productType.item');
      case 'File':
        return t('checkout.productType.file');
      default:
        return type;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return `₪${amount.toFixed(2)}`;
  };

  return (
    <div>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {t('admin.tabs.orders')}
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('admin.orders.sortBy')}</InputLabel>
          <Select value={sortBy} label={t('admin.orders.sortBy')} onChange={handleSortByChange}>
            <MenuItem value="time">{t('admin.orders.sortByTime')}</MenuItem>
            <MenuItem value="amount">{t('admin.orders.sortByAmount')}</MenuItem>
            <MenuItem value="status">{t('admin.orders.sortByStatus')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('admin.orders.sortOrder')}</InputLabel>
          <Select
            value={sortOrder}
            label={t('admin.orders.sortOrder')}
            onChange={handleSortOrderChange}
          >
            <MenuItem value="desc">{t('admin.orders.descending')}</MenuItem>
            <MenuItem value="asc">{t('admin.orders.ascending')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Typography>{t('admin.orders.loading')}</Typography>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Paper>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="orders table">
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin.orders.orderId')}</TableCell>
                  <TableCell>{t('admin.orders.customer')}</TableCell>
                  <TableCell>{t('admin.orders.product')}</TableCell>
                  <TableCell>{t('admin.orders.productType')}</TableCell>
                  <TableCell>{t('admin.orders.amount')}</TableCell>
                  <TableCell>{t('admin.orders.status')}</TableCell>
                  <TableCell>{t('admin.orders.date')}</TableCell>
                  <TableCell>{t('admin.orders.paymentId')}</TableCell>
                  <TableCell>{t('admin.orders.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order._id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {order._id.slice(-8)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {order.customerId ? (
                          <>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {order.customerId.firstName} {order.customerId.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {order.customerId.email}
                            </Typography>
                          </>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontStyle: 'italic' }}
                          >
                            {t('admin.orders.deletedUser')}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {order.productDetails?.image && (
                          <img
                            src={order.productDetails.image}
                            alt={order.productDetails.title}
                            style={{
                              width: 40,
                              height: 40,
                              objectFit: 'cover',
                              borderRadius: 4,
                            }}
                          />
                        )}
                        <Typography variant="body2" sx={{ maxWidth: 200 }}>
                          {order.productDetails?.title || 'Unknown Product'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getProductTypeLabel(order.productType)}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(order.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`admin.orders.${order.status}`)}
                        size="small"
                        color={getStatusColor(order.status) as any}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(order.time)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                      >
                        {order.paymentId ? order.paymentId.slice(-8) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {order.shippingAddress && (
                          <Tooltip title={t('admin.orders.viewAddress')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => setAddressOrder(order)}
                            >
                              <LocationOnIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {order.status === 'preparing' && (
                          <Tooltip title={t('admin.orders.markCompleted')}>
                            <IconButton
                              size="small"
                              color="success"
                              disabled={updatingOrderId === order._id}
                              onClick={() => handleMarkCompleted(order._id)}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={pagination.total}
            rowsPerPage={pagination.limit}
            page={pagination.page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
          />
        </Paper>
      )}

      <Dialog
        open={Boolean(addressOrder)}
        onClose={() => setAddressOrder(null)}
        maxWidth="sm"
        fullWidth
        sx={{ direction: isRTL ? 'rtl' : 'ltr' }}
      >
        <DialogTitle sx={{ textAlign: isRTL ? 'right' : 'left' }}>
          {t('admin.orders.shippingAddress')}
        </DialogTitle>
        <DialogContent sx={{ textAlign: isRTL ? 'right' : 'left' }}>
          {addressOrder?.shippingAddress && (
            <Stack spacing={1}>
              {addressOrder.shippingAddress.fullName && (
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {addressOrder.shippingAddress.fullName}
                </Typography>
              )}
              <Typography variant="body2">
                {addressOrder.shippingAddress.street} {addressOrder.shippingAddress.streetNumber}
                {addressOrder.shippingAddress.flat
                  ? `, ${t('admin.orders.flat')} ${addressOrder.shippingAddress.flat}`
                  : ''}
              </Typography>
              <Typography variant="body2">{addressOrder.shippingAddress.city}</Typography>
              <Typography variant="body2">
                {t('admin.orders.phone')}: {addressOrder.shippingAddress.phone}
              </Typography>
              {addressOrder.shippingAddress.notes && (
                <Typography variant="body2" color="text.secondary">
                  {addressOrder.shippingAddress.notes}
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddressOrder(null)}>{t('common.cancel')}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
